import React from 'react';
import ApptBadge from './ApptBadge';
import icons from './icons';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import useBrandLogo from 'hooks/useBrandLogo';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getCarriers } from 'helpers/appointments/appointments';
import { getCompany } from 'helpers/getCompanyVariables';

const company = getCompany();
const isSunshine = company === 'sunshine';
const isOneill = company === 'oneill';

const ICON_SIZE = 50;

const BRAND_TAGLINE = isSunshine
  ? 'We Care & You Count!'
  : "We're here to empower you.";

// only oneill shows an image (the agent's photo, with the brand mark as fallback);
// sunshine's sidebar already carries the brand, so its banner is text only
const ONEILL_PHOTO_CLASSES = 'size-20 rounded-full max-md:size-14';

export default function ProfileApptBanner({ user }) {
  // the tagline belongs to the portal owner, so an agency running the portal
  // under its own brand gets no tagline rather than somebody else's
  const { isAgencyLogo } = useBrandLogo('logo', 'onDark');
  const tagline = isAgencyLogo ? '' : BRAND_TAGLINE;

  const {
    actionableAppointments,
    requestedAppointments,
    carriers,
    isLoadingAppointmentsData
  } = useSelector(state => state.appointments);

  const appointedCarriers = getCarriers(
    carriers,
    requestedAppointments,
    user,
    ['confirmed'],
    'nameOnly'
  );

  const carrierCount = appointedCarriers.length;
  const pendingApptCount = requestedAppointments.length;
  const actionableApptCount = actionableAppointments.length;

  return (
    <header className='p-4 w-full flex flex-col lg:flex-row items-center justify-between gap-2 bg-[linear-gradient(90deg,var(--color-primary-darkened),var(--color-primary-emphasis-light))] rounded-lg'>
      <section className='flex items-center gap-2'>
        {isOneill && (
          <BrandLogo
            src={user.agentPhoto}
            tone='onDark'
            alt=''
            className={ONEILL_PHOTO_CLASSES}
          />
        )}
        <div className='flex flex-col justify-center leading-[1.2]'>
          <span className='font-semibold text-white select-none text-xl max-md:text-lg'>
            Welcome, {user.First_Name}!
          </span>
          {tagline && (
            <span className='text-lg font-medium select-none text-white max-md:text-base'>
              {tagline}
            </span>
          )}
        </div>
      </section>
      <section
        data-tour='appt-badges'
        className='flex flex-wrap justify-center gap-2'
      >
        <ApptBadge
          icon={
            <icons.confirmed
              color='#7b909d'
              strokeWidth='2.5'
              size={ICON_SIZE}
            />
          }
          label='Confirmed'
          count={carrierCount || 0}
          loading={isLoadingAppointmentsData}
        />
        <ApptBadge
          icon={
            <icons.pending color='#7b909d' strokeWidth='2.5' size={ICON_SIZE} />
          }
          label='Pending'
          count={pendingApptCount || 0}
          loading={isLoadingAppointmentsData}
        />
        {isOneill && (
          <ApptBadge
            icon={
              <icons.needAction
                color='#7b909d'
                strokeWidth='2.5'
                size={ICON_SIZE}
              />
            }
            label='Need Action'
            count={actionableApptCount || 0}
            loading={isLoadingAppointmentsData}
          />
        )}
      </section>
    </header>
  );
}

ProfileApptBanner.propTypes = {
  user: propTypes.object.isRequired
};
